import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { WorkoutProgramDay, useWorkoutPrograms } from "@/hooks/useWorkoutPrograms";
import { Calendar, Clock, Dumbbell, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface WorkoutProgramImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (programDay: WorkoutProgramDay, programTitle: string) => void;
}

export const WorkoutProgramImportModal = ({
  isOpen,
  onClose,
  onImport
}: WorkoutProgramImportModalProps) => {
  const { t } = useTranslation();
  const { programs, loading } = useWorkoutPrograms();

  const handleDaySelect = (day: WorkoutProgramDay, programTitle: string) => {
    onImport(day, programTitle);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t('components.workoutProgramImport.title')}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-2">{t('components.workoutProgramImport.emptyTitle')}</p>
            <p className="text-sm text-muted-foreground">{t('components.workoutProgramImport.emptyHint')}</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              {programs.map((program) => (
                <Card key={program.id}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{program.title}</CardTitle>
                    {program.description && (
                      <p className="text-sm text-muted-foreground">{program.description}</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {program.days.map((day) => (
                        <Button
                          key={day.id}
                          variant="outline"
                          className="w-full justify-start text-left h-auto py-3"
                          onClick={() => handleDaySelect(day, program.title)}
                        >
                          <div className="flex items-start justify-between w-full gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="secondary" className="text-xs">
                                  {t('components.workoutProgramImport.dayPrefix', { number: day.day_number })}
                                </Badge>
                                {day.day_name && (
                                  <span className="font-semibold truncate">{day.day_name}</span>
                                )}
                              </div>
                              <p className="text-sm font-medium truncate">{day.workout_title}</p>
                              {day.workout_description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                  {day.workout_description}
                                </p>
                              )}
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Dumbbell className="h-3 w-3" />
                                  {t('components.workoutProgramImport.exercisesCount', { count: day.exercises.length })}
                                </div>
                                {day.estimated_duration_minutes && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {t('components.workoutProgramImport.minutesShort', { count: day.estimated_duration_minutes })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
